import React, { memo, useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaSlack, FaMicrosoft, FaJira } from 'react-icons/fa6';
import { SiFalcon, SiDatadog } from 'react-icons/si';
import IntegrationCard from '../components/IntegrationCard/IntegrationCard';
import { IntegrationStatus } from '../constants/integrationStatus';
import SlackConfigModal from '../components/IntegrationModal/SlackConfigModal';
import TeamsConfigModal from '../components/IntegrationModal/TeamsConfigModal';
import JiraConfigModal from '../components/IntegrationModal/JiraConfigModal';
import {
  selectAuthIsTeamOwner,
  selectAuthToken,
} from '../redux/selectors/authSelectors';
import {
  selectSlackAvailable,
  selectMsteamsAvailable,
  selectMsteamsInstances,
  selectJiraAvailable,
  selectJiraConfig,
} from '../redux/selectors/integrationsSelectors';
import {
  setIntegrationAvailable,
  setIntegrationData,
} from '../redux/slices/integrationsSlice';
import {
  slackDeauthorize,
  slackRedirect,
  msteamDeauthorize,
  teamsRedirect,
  teamsClientId,
  slackClientId,
  Jira,
} from '../services';
import styled from 'styled-components';

const ConfigPage = memo(() => {
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);
  const isTeamOwner = useSelector(selectAuthIsTeamOwner);
  const slackAvailable = useSelector(selectSlackAvailable);
  const msteamsAvailable = useSelector(selectMsteamsAvailable);
  const msTeamInstances = useSelector(selectMsteamsInstances);
  const jiraAvailable = useSelector(selectJiraAvailable);
  const jiraConfig = useSelector(selectJiraConfig);

  const [configModalId, setConfigModalId] = useState(null);
  const [slackUrl, setSlackUrl] = useState(null);
  const [msTeamUrl, setMsTeamUrl] = useState(null);
  const [isToggling, setIsToggling] = useState(false);

  useEffect(() => {
    setSlackUrl(
      `https://slack.com/oauth/v2/authorize?scope=groups%3Aread%2Cchannels%3Aread%2Cchat%3Awrite%2Epublic%2Cchat%3Awrite&redirect_uri=${slackRedirect()}&client_id=${slackClientId()}&state=${token}`
    );
    setMsTeamUrl(
      `https://login.microsoftonline.com/common/adminconsent?client_id=${teamsClientId()}&state=${token}&redirect_uri=${teamsRedirect()}`
    );
  }, [token]);

  const jiraDisconnect = async () => {
    try {
      await Jira.disconnect();
      return true;
    } catch {
      return false;
    }
  };

  const integrations = [
    {
      id: 'slack',
      name: 'Slack',
      description: 'Push document shares and messages to your Slack channels.',
      category: 'communication',
      icon: FaSlack,
      status: slackAvailable
        ? IntegrationStatus.CONNECTED
        : IntegrationStatus.DISCONNECTED,
      authUrl: slackUrl,
      configUrl: '#/config/slack',
      usageDescription:
        'Receive real-time alerts about emerging security threats, vulnerability disclosures, and curated intelligence directly in your Slack channels.',
      permissions: ['Read channel list', 'Post messages to selected channels'],
      deauthFn: slackDeauthorize,
      ConfigModal: SlackConfigModal,
    },
    {
      id: 'msteams',
      name: 'Microsoft Teams',
      description: 'Push document shares and messages to your Teams channels.',
      category: 'communication',
      icon: FaMicrosoft,
      status:
        msteamsAvailable && msTeamInstances?.length
          ? IntegrationStatus.CONNECTED
          : IntegrationStatus.DISCONNECTED,
      authUrl: msTeamUrl,
      configUrl: '#/config/msteams',
      usageDescription:
        'Receive real-time alerts about emerging security threats, vulnerability disclosures, and curated intelligence directly in your Teams channels.',
      permissions: [
        'Read team and channel list',
        'Post messages to selected channels',
      ],
      deauthFn: msteamDeauthorize,
      ConfigModal: TeamsConfigModal,
    },
    {
      id: 'jira',
      name: 'Jira',
      description:
        'Create and track security issues directly in your Jira projects.',
      category: 'project management',
      icon: FaJira,
      status: jiraAvailable
        ? IntegrationStatus.CONNECTED
        : IntegrationStatus.DISCONNECTED,
      authUrl: null,
      configUrl: '#/config/jira',
      usageDescription:
        'Monitor and enrich Jira security tickets with the latest news developments, analysis, and mitigation content.',
      permissions: ['Read projects and issues', 'Create comments on issues'],
      deauthFn: jiraDisconnect,
      ConfigModal: JiraConfigModal,
      // JIRA uses form-based setup, not OAuth redirect
      usesFormSetup: true,
    },
    // {
    //   id: 'crowdstrike',
    //   name: 'CrowdStrike',
    //   description: 'Integrate threat intelligence from CrowdStrike Falcon platform.',
    //   category: 'security',
    //   icon: SiFalcon,
    //   status: IntegrationStatus.UPCOMING,
    //   authUrl: null,
    //   configUrl: '#/config/crowdstrike',
    //   usageDescription: 'Correlate threat intelligence from CrowdStrike Falcon with emerging security news and vulnerability disclosures.',
    //   permissions: ['Read detection and incident data', 'Access threat intelligence feeds']
    // },
    // {
    //   id: 'datadog',
    //   name: 'Datadog',
    //   description: 'Send security metrics and alerts to your Datadog dashboards.',
    //   category: 'monitoring',
    //   icon: SiDatadog,
    //   status: IntegrationStatus.UPCOMING,
    //   authUrl: null,
    //   configUrl: null,
    //   usageDescription: 'Push security metrics and alert data to your Datadog dashboards for centralized monitoring and visualization.',
    //   permissions: ['Write metrics', 'Create events']
    // }
  ];

  const handleToggle = async (id) => {
    if (isToggling) return;
    const integration = integrations.find((i) => i.id === id);

    if (integration.status === IntegrationStatus.CONNECTED) {
      // Disconnect
      setIsToggling(true);
      try {
        const success = await integration.deauthFn(token);

        if (success) {
          toast.success(`Success: ${integration.name} has been disconnected`);
          dispatch(
            setIntegrationData({
              id,
              data: { channels: [], instances: [], meta: {} },
            })
          );
          dispatch(setIntegrationAvailable({ id, available: false }));
        } else {
          toast.error(
            `Error: There was a problem disconnecting ${integration.name}`
          );
        }
      } catch {
        toast.error(
          `Error: There was a problem disconnecting ${integration.name}`
        );
      } finally {
        setIsToggling(false);
      }
    } else if (integration.usesFormSetup) {
      // Form-based integrations (JIRA): open config modal for setup
      setConfigModalId(id);
    } else {
      // OAuth-based integrations (Slack, Teams): redirect
      window.location.href = integration.authUrl;
    }
  };

  const handleConfigure = (id) => {
    const integration = integrations.find((i) => i.id === id);
    if (integration.ConfigModal) {
      // For form-based integrations, allow configure even when disconnected
      if (
        integration.usesFormSetup ||
        integration.status === IntegrationStatus.CONNECTED
      ) {
        setConfigModalId(id);
      }
    }
  };

  if (!isTeamOwner) {
    return <div>Access denied. Team owner privileges required.</div>;
  }

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <h2>Integrations</h2>
          <p>
            Connect your communication platforms to receive alerts and share
            documents.
          </p>
        </PageHeader>

        <Row xs={1} md={2} lg={3} className='g-4'>
          {integrations.map((item) => (
            <Col key={item.id}>
              <IntegrationCard
                {...item}
                onToggle={handleToggle}
                onConfigure={handleConfigure}
              />
            </Col>
          ))}
        </Row>
      </Container>

      {integrations.map(({ id, ConfigModal, status }) =>
        ConfigModal ? (
          <ConfigModal
            key={id}
            isOpen={configModalId === id}
            onClose={() => setConfigModalId(null)}
            onSave={() => setConfigModalId(null)}
            isConnected={status === IntegrationStatus.CONNECTED}
            currentConfig={id === 'jira' ? jiraConfig : undefined}
          />
        ) : null
      )}
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  padding: 20px 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;

  h2 {
    font-family: 'Titillium Web', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    color: #000;
    margin-bottom: 8px;
  }

  p {
    font-size: 0.875rem;
    color: #a3a5a9;
    margin: 0;
  }
`;

ConfigPage.displayName = 'ConfigPage';

export default ConfigPage;
